import React from 'react';

import {
  AttendeeFragment,
  InteractionDetails,
  InteractionForAttendeeFragment,
  InteractionFragment,
  InteractionValueType,
  PublicEWebinarFragment,
  ThinkificPromoInteractionDetails,
} from '$/GraphQL/schema/schema';
import { InteractionInfo, NoBodyCard } from '$/components/Interactions/InteractionInfo';
import {
  InteractionModalAccordionsHook,
  InteractionModalComponentProps,
  ModalValidationRules,
} from '$/components/Interactions/types';
import type { IntegrationInfo } from '..';

import { StyleProps } from '$/components/ui';
import { ThinkificCardIcon } from '$/images/FontAwesomeIcons';
import { AnalyticsCardProps } from '$/components/Interactions/cards/types';

import { ThinkificPromoCard } from './ThinkificPromoCard';
import { useThinkificPromoInteractionSettings } from './useThinkificPromoInteractionSettings';
import { ThinkificPromoInteractionValidationRules } from './ThinkificPromoInteractionValidationRules';

const AnalyticsClickCard = React.lazy(
  () => import('$/components/Interactions/cards/AnalyticsClickCard')
);
const ThinkificPromoInteractionModal = React.lazy(() => import('./ThinkificPromoInteractionModal'));

export class ThinkificPromoInteractionInfo extends InteractionInfo {
  constructor(
    i: InteractionForAttendeeFragment | InteractionFragment,
    integration?: IntegrationInfo,
    ewebinar?: PublicEWebinarFragment,
    attendee?: AttendeeFragment
  ) {
    super(i, ewebinar, attendee);
    this.integration = integration;
  }

  public typeName(): string {
    return 'Thinkific offer';
  }

  public title(): string {
    return this.i.details?.title ?? '';
  }

  public subtitle(): string | undefined {
    return this.i.details?.subtitle ?? undefined;
  }

  public description(): string {
    return this.i.details?.description ?? '';
  }

  public detailsDefault(interactions: InteractionInfo[]): InteractionDetails {
    const defaults = super.detailsDefault(interactions);
    const integration: ThinkificPromoInteractionDetails = {
      keepInSync: true,
    };

    return {
      ...defaults,
      integration,
    };
  }

  public hasAnswers(): boolean {
    return true;
  }

  public hasExpiresInFieldToggle(): boolean {
    return true;
  }

  public hasExpiresInField(): boolean {
    return true;
  }

  public hasExpiresInReplayToggle(): boolean {
    return true;
  }

  public valueType(): InteractionValueType {
    return InteractionValueType.Action;
  }

  public icon(): React.FC<StyleProps> {
    return ThinkificCardIcon;
  }

  public cardComponent(): React.FC<{ onClickHeader?: (() => void) | undefined }> {
    return ThinkificPromoCard;
  }

  public cardBodyComponent(): React.FC {
    return NoBodyCard;
  }

  public modalValidationRules(): ModalValidationRules {
    return ThinkificPromoInteractionValidationRules();
  }

  public useModalPreviewComponent(): React.LazyExoticComponent<
    React.FC<InteractionModalComponentProps>
  > | null {
    return ThinkificPromoInteractionModal;
  }

  public modalSettingsAccordions(): InteractionModalAccordionsHook {
    return useThinkificPromoInteractionSettings;
  }

  public canSubmitMuktipleTimes(): boolean {
    return true;
  }

  public analyticsCardComponent(): React.LazyExoticComponent<React.FC<AnalyticsCardProps>> {
    return AnalyticsClickCard;
  }

  public requiresHeaderMedia(): boolean {
    return true;
  }
}
